import { useMemo } from 'react';

import Location, { LocationDetail } from 'dashboard/components/Location';
import { MapMarker } from 'dashboard/components/Map';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { EnvironmentType } from 'dashboard/models/environment';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { Report } from 'dashboard/models/report';
import { ReportProfile } from 'dashboard/models/report/profile';
import {
  Verification,
  VerificationDatabase,
  VerificationGovernmentId,
  VerificationPhoneNumber,
} from 'dashboard/models/verification';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';

interface InquiryDetailsLocationProps {
  isRedacted: boolean;
  reports: Report[];
  sessions: Record<string, InquirySession>;
  verifications: Verification[];
  matchUrl: string;
  environmentType: EnvironmentType;
}

const InquiryDetailsLocation = ({
  isRedacted,
  reports,
  sessions,
  verifications,
  matchUrl,
  environmentType,
}: InquiryDetailsLocationProps) => {
  /**
   * Filter all our data inputs to the ones containing location data.
   */
  const locations = useMemo<LocationDetail[]>(
    () =>
      [
        ...reports,
        ...Object.values(sessions),
        ...verifications.filter((v) =>
          [
            VerificationStatus.Passed,
            VerificationStatus.RequiresRetry,
            VerificationStatus.Failed,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ].includes(v.attributes.status),
        ),
      ]
        .filter(
          (entity) =>
            entity.type === ApiModel.VerificationGovernmentId ||
            entity.type === ApiModel.VerificationDatabase ||
            entity.type === ApiModel.InquirySession ||
            entity.type === ApiModel.ReportProfile,
        )
        .flatMap((entity: Parameters<typeof formatEntityAsLocationDetails>[0]): LocationDetail[] =>
          formatEntityAsLocationDetails(entity).flatMap((l: LocationDetail) =>
            // Pending objects sometimes have undefined lat/lng; don't display these.
            l.lat != null && l.lng != null && l.location.length > 0 ? l : [],
          ),
        ),
    [reports, sessions, verifications],
  );

  return (
    <Location
      isRedacted={isRedacted}
      locations={locations}
      matchUrl={matchUrl}
      environmentType={environmentType}
      customLocations={undefined}
    />
  );
};

export default InquiryDetailsLocation;

export function formatBasicEntityAsLocationDetail(
  entity: VerificationPhoneNumber | VerificationGovernmentId | VerificationDatabase | ReportProfile,
): LocationDetail {
  const {
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'country-code': countryCode,
  } = entity.attributes;

  const cityAndSubdivision = [addressCity, addressSubdivision].filter(Boolean).join(', ');
  const cityAndSubdivisionAndPostalCode = [cityAndSubdivision, addressPostalCode]
    .filter(Boolean)
    .join(' ');

  // We currently cannot create list matches on Reports since they run asynchronously
  const listMatchCount =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    entity.type !== ApiModel.ReportProfile ? entity.relationships['list-matches'].data.length : 0;

  return {
    id: entity.id,
    type: entity.type,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    location: [addressStreet1, addressStreet2, cityAndSubdivisionAndPostalCode].filter(Boolean),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    country: countryCode,
    listMatchCount: listMatchCount,
    ...MapMarker.fromBasicGeocodedEntity(entity),
  };
}

export function formatSessionAsLocationDetail(
  session: InquirySession,
  config?: { useGps: boolean },
): LocationDetail {
  const {
    latitude,
    longitude,
    'gps-latitude': gpsLatitude,
    'gps-longitude': gpsLongitude,
    'region-name': regionName,
    'country-code': countryCode,
  } = session.attributes;

  const { 'list-matches': listMatches } = session.relationships;
  const listMatchCount = listMatches.data.length;

  const [lat, lng, rgn, country] = config?.useGps
    ? [gpsLatitude, gpsLongitude, undefined, undefined]
    : [latitude, longitude, regionName, countryCode];

  let formattedCoordinates = undefined;
  if (lat != null && lng != null) {
    formattedCoordinates = `${stringifyLatitude(lat)} ${stringifyLongitude(lng)}`;
  }

  return {
    id: session.id,
    type: session.type,
    location: [formattedCoordinates, rgn].filter(Boolean) as string[],
    country: country ?? '',
    listMatchCount: listMatchCount,
    countryMismatch: false,
    ...MapMarker.fromInquirySession(session, { useGps: !!config?.useGps }),
  };
}

function formatEntityAsLocationDetails(
  entity:
    | ReportProfile
    | InquirySession
    | VerificationPhoneNumber
    | VerificationGovernmentId
    | VerificationDatabase,
): LocationDetail[] {
  switch (entity.type) {
    // Basic
    case ApiModel.VerificationPhoneNumber:
    case ApiModel.VerificationDatabase:
    case ApiModel.ReportProfile:
      return [formatBasicEntityAsLocationDetail(entity)];
    // Documents
    case ApiModel.VerificationGovernmentId:
      return [
        {
          ...formatBasicEntityAsLocationDetail(entity),
          startDate: entity.attributes['issue-date'],
          endDate: entity.attributes['expiration-date'],
        },
      ];
    // Network -- will be deprecated
    case ApiModel.InquirySession:
      return [
        formatSessionAsLocationDetail(entity),
        formatSessionAsLocationDetail(entity, { useGps: true }),
      ];
    default:
      assertNever(entity);
  }
}
